// ActiveTrades.jsx — Industry dark rewrite (XAUPilot).
// The VISUAL layer is fully replaced with the blueprint price-ladder card:
// to-scale SL · Entry · live · TP, live P&L in pips, R:R and progress-to-target.
// ALL original data logic is preserved verbatim — service calls, freemium 4h lock
// + countdown, pip math, new-signal notifications, filled/near-target states.
//
// Deps: lucide-react (already in your app) + your services. No MUI needed.
// Needs Barlow + Barlow Condensed loaded (industry.css / the theme).

import React, { useState, useEffect, useRef } from 'react';
import {
  TrendingUp, TrendingDown, Lock, Clock, Zap, Check, X, Activity, Bell, ShieldCheck,
} from 'lucide-react';
import tradeService from '../services/tradeService';
import authService from '../services/authService';
import NotificationService from '../services/NotificationService';

const C = {
  bg: '#15171a', surface: '#1e2126', text: '#eceef0',
  dim: 'rgba(236,238,240,0.6)', faint: 'rgba(236,238,240,0.42)',
  divider: 'rgba(236,238,240,0.15)', neutral: 'rgba(236,238,240,0.10)',
  accent: '#5980a6', accent400: '#7ba0c4',
  pos: '#46a07a', posInk: '#86c9a6', neg: '#cf6f61', negInk: '#e59a8f',
  head: '"Barlow Condensed", "Barlow", system-ui, sans-serif',
  body: '"Barlow", system-ui, -apple-system, sans-serif',
};

// ---------------------------------------------------------------------------
// LOGIC — unchanged from the original
// ---------------------------------------------------------------------------
const formatTimeRemaining = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m ${remainingSeconds}s`;
};

const calculateTradeProgress = (trade) => {
  if (!trade || trade.isLocked || trade.entry === 'Locked') return null;
  try {
    const entry = parseFloat(trade.entry);
    const current = parseFloat(trade.current);
    const tp = parseFloat(trade.takeProfit);
    const sl = parseFloat(trade.stopLoss);
    if (trade.type === 'BUY') {
      if (current > entry) {
        const tpProgress = ((current - entry) / (tp - entry)) * 100;
        return { type: 'profit', progress: Math.min(tpProgress, 100), pips: ((current - entry) * 10).toFixed(0), isNearTarget: tpProgress > 75 };
      } else {
        const slProgress = ((entry - current) / (entry - sl)) * 100;
        return { type: 'loss', progress: Math.min(slProgress, 100), pips: ((current - entry) * 10).toFixed(0), isNearTarget: slProgress > 75 };
      }
    } else { // SELL
      if (current < entry) {
        const tpProgress = ((entry - current) / (entry - tp)) * 100;
        return { type: 'profit', progress: Math.min(tpProgress, 100), pips: ((entry - current) * 10).toFixed(0), isNearTarget: tpProgress > 75 };
      } else {
        const slProgress = ((current - entry) / (sl - entry)) * 100;
        return { type: 'loss', progress: Math.min(slProgress, 100), pips: ((entry - current) * 10).toFixed(0), isNearTarget: slProgress > 75 };
      }
    }
  } catch (e) { return null; }
};

// ---------------------------------------------------------------------------
// PRESENTATION helpers
// ---------------------------------------------------------------------------
const formatAge = (iso) => {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h${String(m).padStart(2, '0')}m` : `${m}m`;
};

const rrRatio = (t) => {
  const e = parseFloat(t.entry), sl = parseFloat(t.stopLoss), tp = parseFloat(t.takeProfit);
  const risk = Math.abs(e - sl), reward = Math.abs(tp - e);
  if (!risk || isNaN(risk) || isNaN(reward)) return '—';
  return `1 : ${(reward / risk).toFixed(1)}`;
};

const cs = (v, h) => ({
  position: 'absolute', width: 7, height: 7, pointerEvents: 'none',
  [v]: -1, [h]: -1,
  [v === 'top' ? 'borderTop' : 'borderBottom']: `1px solid ${C.accent}`,
  [h === 'left' ? 'borderLeft' : 'borderRight']: `1px solid ${C.accent}`,
});
const Corners = () => (
  <><i style={cs('top', 'left')} /><i style={cs('top', 'right')} /><i style={cs('bottom', 'left')} /><i style={cs('bottom', 'right')} /></>
);

const label = (txt, color) => (
  <span style={{ font: `600 9px/1 ${C.head}`, letterSpacing: '0.1em', color }}>{txt}</span>
);

// A to-scale price ladder for one active (unlocked, unfilled) trade
const Ladder = ({ trade, progress }) => {
  const entry = parseFloat(trade.entry), current = parseFloat(trade.current);
  const tp = parseFloat(trade.takeProfit), sl = parseFloat(trade.stopLoss);
  const lo = Math.min(entry, current, tp, sl), hi = Math.max(entry, current, tp, sl);
  const range = (hi - lo) || 1;
  const lo2 = lo - range * 0.08, hi2 = hi + range * 0.08;
  const pos = (p) => ((p - lo2) / (hi2 - lo2)) * 100;
  const isProfit = progress?.type === 'profit';
  const pe = pos(entry), pc = pos(current);
  const barBottom = Math.min(pe, pc), barH = Math.abs(pc - pe);
  const val = (v) => (<span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{v}</span>);
  const RowMark = ({ p, tick, lab, labColor, valColor, value }) => (
    <div style={{ position: 'absolute', left: 0, right: 0, bottom: `${p}%`, transform: 'translateY(50%)', display: 'flex', alignItems: 'center', gap: 7 }}>
      <span style={{ width: 8, height: 1, background: tick, flex: 'none' }} />
      {label(lab, labColor)}
      <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: valColor }}>{value}</span>
    </div>
  );
  return (
    <div style={{ position: 'relative', width: 150, height: 158, flex: 'none' }}>
      <div style={{ position: 'absolute', left: 3, top: 4, bottom: 4, width: 1, background: C.divider }} />
      <div style={{ position: 'absolute', left: 2, width: 3, bottom: `${barBottom}%`, height: `${barH}%`, background: isProfit ? C.pos : C.neg }} />
      <RowMark p={pos(tp)} tick={C.accent} lab="TP" labColor={C.accent} valColor={C.posInk} value={trade.takeProfit} />
      <RowMark p={pos(entry)} tick="rgba(236,238,240,0.45)" lab="ENTRY" labColor={C.dim} valColor={C.text} value={trade.entry} />
      <RowMark p={pos(sl)} tick={C.neg} lab="SL" labColor={C.negInk} valColor={C.negInk} value={trade.stopLoss} />
      {/* live current marker */}
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: `${pc}%`, transform: 'translateY(50%)', display: 'flex', alignItems: 'center' }}>
        <span style={{ flex: 1, height: 1.5, background: C.accent }} />
        <span style={{ fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: C.bg, background: C.accent, padding: '2px 6px' }}>{trade.current}</span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
const ActiveTrades = () => {
  const [activeTrades, setActiveTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isPremium, setIsPremium] = useState(false);
  const [subscriptionLevel, setSubscriptionLevel] = useState('free');
  const [nowTs, setNowTs] = useState(Date.now());
  const timersRef = useRef({});

  const fetchActiveTrades = async () => {
    try {
      const response = await tradeService.getActiveTrades();
      setActiveTrades(response.trades);
      setIsPremium(response.premium_access);
      setSubscriptionLevel(response.subscription_level);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load active trades', err);
      setError('Failed to load active trades. Please try again later.');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveTrades();
    const refreshInterval = setInterval(fetchActiveTrades, 30000);
    const signalCheckInterval = setInterval(checkForNewSignals, 15000);
    const clockInterval = setInterval(() => setNowTs(Date.now()), 30000);
    return () => {
      clearInterval(refreshInterval);
      clearInterval(signalCheckInterval);
      clearInterval(clockInterval);
      Object.values(timersRef.current).forEach((timer) => clearInterval(timer));
    };
  }, []);

  const checkForNewSignals = async () => {
    try {
      const response = await tradeService.getActiveTrades();
      const trades = response.trades;
      if (!trades || trades.length === 0) return;
      const newestTrade = trades[0];
      const newestTradeTimestamp = new Date(newestTrade.created_at).getTime();
      const lastSignalTimestamp = NotificationService.getLastSignalTimestamp();
      if (newestTradeTimestamp > lastSignalTimestamp) {
        NotificationService.setLastSignalTimestamp(newestTradeTimestamp);
        if (NotificationService.areNotificationsEnabled() && document.visibilityState === 'visible') {
          new Notification(`New ${newestTrade.type} Signal: ${newestTrade.symbol}`, {
            body: `Entry: ${newestTrade.entry}, Take Profit: ${newestTrade.takeProfit}, Stop Loss: ${newestTrade.stopLoss}`,
            icon: '/favicon.ico',
          });
        }
      }
    } catch (err) {
      console.error('Error checking for new signals:', err);
    }
  };

  // Countdown timers for locked trades
  useEffect(() => {
    Object.values(timersRef.current).forEach((timer) => clearInterval(timer));
    timersRef.current = {};
    activeTrades.forEach((trade) => {
      if (trade.isLocked && trade.time_remaining > 0) {
        const tradeId = trade.id;
        timersRef.current[tradeId] = setInterval(() => {
          setActiveTrades((prevTrades) => prevTrades.map((prevTrade) => {
            if (prevTrade.id === tradeId) {
              const newTimeRemaining = Math.max(0, prevTrade.time_remaining - 1);
              if (newTimeRemaining === 0) {
                clearInterval(timersRef.current[tradeId]);
                delete timersRef.current[tradeId];
                fetchActiveTrades();
                return { ...prevTrade, isLocked: false, time_remaining: 0 };
              }
              return { ...prevTrade, time_remaining: newTimeRemaining };
            }
            return prevTrade;
          }));
        }, 1000);
      }
    });
    return () => { Object.values(timersRef.current).forEach((timer) => clearInterval(timer)); };
  }, [activeTrades]);

  const shell = (children) => (
    <div className="xau-at" style={{ fontFamily: C.body, color: C.text }}>
      <style>{`
        @keyframes xauPulse { 0%,100%{opacity:1} 50%{opacity:.2} }
        .xau-at .xau-dot { animation: xauPulse 1.8s ease-in-out infinite; }
        .xau-at .xau-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:20px; }
        @media (max-width: 900px){ .xau-at .xau-grid { grid-template-columns:1fr; } }
        .xau-at .xau-card { transition: border-color .15s; }
        .xau-at .xau-card:hover { border-color: color-mix(in srgb, ${C.accent} 45%, ${C.divider}); }
        .xau-at .xau-unlock:hover { background:${C.accent400} !important; color:${C.bg} !important; }
      `}</style>
      {children}
    </div>
  );

  const banner = (icon, text, sub) => shell(
    <div style={{ position: 'relative', border: `1px solid ${C.divider}`, background: C.surface, padding: '28px 24px', display: 'flex', alignItems: 'center', gap: 14 }}>
      <Corners />
      {icon}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ font: `600 15px/1 ${C.head}` }}>{text}</span>
        {sub && <span style={{ fontSize: 13, color: C.dim }}>{sub}</span>}
      </div>
    </div>
  );

  if (loading) return banner(<Activity size={20} color={C.accent} className="xau-dot" />, 'Loading active trades', 'Fetching the latest market signals…');
  if (error) return banner(<X size={20} color={C.negInk} />, 'Could not load trades', error);
  if (activeTrades.length === 0) return banner(<Activity size={20} color={C.accent} />, 'No active trades available', 'New signals will appear here when available. Check back soon.');

  return shell(
    <>
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <h3 style={{ margin: 0, fontFamily: C.head, fontSize: 22, fontWeight: 700, letterSpacing: '0.01em' }}>Active Signals</h3>
          <span style={{ fontSize: 12, color: C.dim }}>Live market signals · XAU/USD</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {isPremium ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', border: `1px solid ${C.accent}`, color: C.accent, font: `600 11px/1 ${C.head}`, letterSpacing: '0.06em' }}><ShieldCheck size={13} />PREMIUM</span>
          ) : (
            <span title="Free users see trade details 4 hours after they are posted" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', background: C.neutral, color: C.text, font: `600 11px/1 ${C.head}`, letterSpacing: '0.06em' }}><Clock size={13} />4-HOUR DELAY</span>
          )}
          <span style={{ fontSize: 11, color: C.faint, fontVariantNumeric: 'tabular-nums' }}>Updated {new Date(nowTs).toLocaleTimeString()}</span>
        </div>
      </div>

      {/* Cards */}
      <div className="xau-grid">
        {activeTrades.map((trade, index) => {
          const progress = calculateTradeProgress(trade);
          const isBuy = trade.type === 'BUY';
          const dirIcon = isBuy ? <TrendingUp size={12} /> : <TrendingDown size={12} />;
          const pipsNum = progress ? parseInt(progress.pips, 10) : 0;
          const pipsStr = (pipsNum >= 0 ? '+' : '−') + Math.abs(pipsNum);
          const pipsColor = progress?.type === 'profit' ? C.posInk : C.negInk;
          const toLabel = progress?.type === 'profit' ? 'To Target' : 'To Stop';
          const toColor = progress?.type === 'profit' ? C.posInk : C.negInk;
          let statusText = '', statusColor = C.dim;
          if (progress) {
            if (progress.isNearTarget) { statusText = progress.type === 'profit' ? 'Approaching take-profit' : 'Approaching stop-loss'; statusColor = progress.type === 'profit' ? C.posInk : C.negInk; }
            else { statusText = progress.type === 'profit' ? 'In profit' : 'Below entry · watching stop'; }
          }

          return (
            <article key={trade.id || index} className="xau-card" style={{ position: 'relative', border: `1px solid ${trade.isFilled ? C.divider : (progress?.isNearTarget ? (progress.type === 'profit' ? C.pos : C.neg) : C.divider)}`, background: C.surface, padding: 18, display: 'flex', flexDirection: 'column', gap: 14, opacity: trade.isFilled ? 0.72 : 1 }}>
              <Corners />

              {/* Header row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                {trade.isLocked ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 9px', border: `1px solid ${C.divider}`, color: C.dim, font: `600 11px/1 ${C.head}`, letterSpacing: '0.06em' }}><Lock size={12} />LOCKED</span>
                ) : isBuy ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 9px', background: C.accent, color: C.text, font: `600 11px/1 ${C.head}`, letterSpacing: '0.06em' }}>{dirIcon}BUY</span>
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 9px', border: `1px solid ${C.accent}`, color: C.accent, font: `600 11px/1 ${C.head}`, letterSpacing: '0.06em' }}>{dirIcon}SELL</span>
                )}
                <span style={{ font: `600 15px/1 ${C.head}`, letterSpacing: '0.02em' }}>{trade.symbol}</span>
                {trade.isFilled ? (
                  <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10, letterSpacing: '0.06em', color: trade.filledSuccess ? C.posInk : C.negInk }}>
                    {trade.filledSuccess ? <Check size={13} /> : <X size={13} />}
                    {trade.filledReason === 'take_profit' ? 'TP HIT' : 'SL HIT'}
                  </span>
                ) : trade.isLocked ? (
                  <span style={{ marginLeft: 'auto', fontSize: 10, letterSpacing: '0.06em', color: C.faint }}>POSTED {formatAge(trade.created_at)} AGO</span>
                ) : (
                  <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10, letterSpacing: '0.06em', color: progress?.type === 'loss' ? C.negInk : C.posInk }}>
                    <span className="xau-dot" style={{ width: 6, height: 6, background: progress?.type === 'loss' ? C.neg : C.pos, display: 'inline-block' }} />LIVE · {formatAge(trade.created_at)}
                  </span>
                )}
              </div>

              {/* Body */}
              {trade.isLocked ? (
                <div style={{ display: 'flex', gap: 16, alignItems: 'stretch' }}>
                  <div style={{ position: 'relative', width: 150, height: 158, flex: 'none', display: 'grid', placeItems: 'center', border: `1px dashed ${C.divider}`, background: 'repeating-linear-gradient(135deg, transparent, transparent 6px, rgba(236,238,240,0.06) 6px, rgba(236,238,240,0.06) 7px)' }}>
                    <Lock size={26} color={C.faint} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 12 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <span style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.dim }}>Unlocks in</span>
                      <span style={{ fontFamily: C.head, fontSize: 26, fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{formatTimeRemaining(trade.time_remaining)}</span>
                      <span style={{ fontSize: 11, color: C.faint }}>Premium users get instant access</span>
                    </div>
                    <div style={{ height: 6, background: C.neutral, position: 'relative' }}>
                      <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${(1 - trade.time_remaining / 14400) * 100}%`, background: C.accent }} />
                    </div>
                    {subscriptionLevel === 'free' && (
                      <button className="xau-unlock" onClick={() => { window.location.href = authService.isAuthenticated() ? '/subscription' : '/login'; }} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, width: '100%', padding: '9px 14px', background: C.accent, color: C.text, border: 'none', font: `600 13px/1 ${C.head}`, letterSpacing: '0.04em', cursor: 'pointer' }}>
                        <Zap size={14} />Upgrade for instant access
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 16 }}>
                  <Ladder trade={trade} progress={progress} />
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontFamily: C.head, fontSize: 32, fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums', color: trade.isFilled ? C.dim : pipsColor }}>{pipsStr}</span>
                      <span style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.dim }}>pips · {trade.isFilled ? 'realized' : 'unrealized'}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 18, fontVariantNumeric: 'tabular-nums' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.dim }}>R : R</span>
                        <span style={{ fontSize: 14, fontWeight: 600 }}>{rrRatio(trade)}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.dim }}>{toLabel}</span>
                        <span style={{ fontSize: 14, fontWeight: 600, color: toColor }}>{progress ? `${Math.round(progress.progress)}%` : '—'}</span>
                      </div>
                    </div>
                    {isPremium && progress && !trade.isFilled ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <div style={{ height: 6, background: C.neutral, position: 'relative' }}>
                          <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${progress.progress}%`, background: progress.type === 'profit' ? C.pos : C.neg }} />
                        </div>
                        <span style={{ fontSize: 10, color: statusColor, letterSpacing: '0.02em' }}>{statusText}</span>
                      </div>
                    ) : trade.isFilled ? (
                      <span style={{ fontSize: 11, color: C.dim, fontVariantNumeric: 'tabular-nums' }}>{trade.filledReason === 'take_profit' ? 'Take profit hit' : 'Stop loss hit'}{trade.filledAt ? ` · ${new Date(trade.filledAt).toLocaleTimeString()}` : ''}</span>
                    ) : null}
                  </div>
                </div>
              )}

              {/* Footer timestamp */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, paddingTop: 12, borderTop: `1px solid ${C.divider}`, fontVariantNumeric: 'tabular-nums' }}>
                <span style={{ fontSize: 11, color: C.faint }}>{new Date(trade.created_at).toLocaleString()}{trade.updated_at !== trade.created_at ? ' · Updated' : ''}</span>
                {!trade.isLocked && progress && !trade.isFilled && (
                  <span style={{ fontSize: 10, letterSpacing: '0.06em', color: progress.type === 'profit' ? C.posInk : C.negInk }}>{progress.type === 'profit' ? 'IN PROFIT' : 'IN LOSS'}</span>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </>
  );
};

export default ActiveTrades;
